import React from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

const articleTopic = (article) => {
  if (article?.tags && article.tags.length > 0) return article.tags[0];
  return 'general';
};

const articleTime = (article) => {
  if (!article?.published) return 'recent';
  try {
    return formatDistanceToNow(new Date(article.published), { addSuffix: true });
  } catch {
    return 'unknown date';
  }
};

const safeHttpUrl = (url) => (/^https?:\/\//i.test(url ?? '') ? url : null);

const truncateBlurb = (text, max = 180) => {
  if (!text) return null;
  const clean = String(text).replace(/\s+/g, ' ').trim();
  if (!clean) return null;
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max).trim()}…`;
};

const MAX_OUTLET_CHIPS = 6;

const ArticleCard = ({ article, saved = false, onToggleSave }) => {
  if (!article) {
    return (
      <div className="pb-row" aria-hidden="true">
        <div className="pb-row-headline">No article data</div>
      </div>
    );
  }

  const tag = articleTopic(article);
  const time = articleTime(article);
  const sourceName = article.source_name || 'unknown source';
  const tagList = (article.tags || []).filter(Boolean);
  const blurb = truncateBlurb(article.summary);
  const sourceLink = safeHttpUrl(article.link);

  const secondaryChips = tagList.filter(
    (t) => t.toLowerCase() !== tag.toLowerCase(),
  );
  const visibleSecondary = secondaryChips.slice(0, MAX_OUTLET_CHIPS - 1);
  const overflow = secondaryChips.length - visibleSecondary.length;
  const sourceCount = 1;
  const sourceLabel = sourceCount === 1 ? 'SOURCE' : 'SOURCES';

  const handleSave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (typeof onToggleSave === 'function') {
      onToggleSave(article.id);
    }
  };

  return (
    <article className="pb-row">
      <div className="pb-row-top">
        <span className="pb-mono-tag pb-row-tag">
          ▸ {tag.toUpperCase()} · {sourceCount} {sourceLabel} · {time}
        </span>
      </div>

      <h3 className="pb-row-headline">
        <Link
          to={`/article/${article.id}`}
          className="pb-row-link"
          aria-label={`Story: ${article.title || 'Untitled story'}`}
        >
          {article.title || 'Untitled story'}
        </Link>
      </h3>

      {blurb && <p className="pb-row-blurb">{blurb}</p>}

      <div className="pb-row-bottom">
        <div className="pb-outlets pb-row-outlet-strip">
          {sourceLink ? (
            <a
              href={sourceLink}
              target="_blank"
              rel="noopener noreferrer"
              className="pb-outlet-chip"
            >
              {sourceName} ↗
            </a>
          ) : (
            <span className="pb-outlet-chip">{sourceName}</span>
          )}
          {visibleSecondary.map((chip) => (
            <span key={chip} className="pb-outlet-chip">
              {chip}
            </span>
          ))}
          {overflow > 0 && (
            <span className="pb-outlet-chip--more">+{overflow} more</span>
          )}
        </div>
        {typeof onToggleSave === 'function' && (
          <button
            type="button"
            onClick={handleSave}
            className={`pb-save-button ${saved ? 'is-saved' : ''}`}
            aria-label={saved ? `Unsave ${article.title}` : `Save ${article.title}`}
            aria-pressed={saved}
            data-saved={saved ? 'true' : 'false'}
          >
            {saved ? '★' : '☆'}
          </button>
        )}
      </div>
    </article>
  );
};

export default ArticleCard;
